import React, { useState } from "react";
import {
  ArrowBigDown,
  ArrowBigUp,
  ChefHat,
  Loader2,
  MessageSquare,
  User,
} from "lucide-react";
import { OrderTypeBadge } from "../../../components/OrderTypeBadge";
import type { PainelFilaItem } from "../../../services/painelService";
import { TemposEtapasBadges } from "./TemposEtapasBadges";
import {
  segundosDesdeInicio,
  segundosEntreInstantes,
} from "../utils/temposPainel";
import {
  filaDescricaoJsonParaTexto,
  formatPainelDescricaoResumo,
} from "../utils/descricaoPainel";
import {
  resolvePainelCardUrgencia,
  resolveTempoColunaFilaMontagem,
} from "../utils/painelCardUrgencia";
import { PainelPedidoOriginalButton } from "./PainelPedidoOriginalButton";
import { FilaItemObservacaoModal } from "./FilaItemObservacaoModal";
import { PAINEL_ICON_BTN_CLASS, PAINEL_ICON_SIZE } from "./PainelIconButton";
import { PainelCardActionRow } from "./PainelCardActionRow";
import {
  PainelCardAmberNote,
  PainelCardAmberObsPizza,
  PainelCardDetailRow,
  PainelCardShell,
  PainelCardTemposSection,
} from "./PainelCardShell";
import {
  filaTipoLabel,
  isFilaDoceFamilia,
} from "../../../services/filaPizzasService";
import {
  dedupeObservacoes,
  isPedidoSalaoFoodfly,
  resolveSalaoProducaoLabels,
} from "../utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";

interface CardFilaMontagemProps {
  item: PainelFilaItem;
  nowMs: number;
  isPrioritario?: boolean;
  showPriorizarButton?: boolean;
  isPriorizando?: boolean;
  isRemovendo?: boolean;
  onPriorizar?: () => void;
  onRemover?: () => void;
  onVerPedidoOriginal?: (orderId: number) => void;
  onRefetchPainel?: () => Promise<void>;
}

export const CardFilaMontagem: React.FC<CardFilaMontagemProps> = ({
  item,
  nowMs,
  isPrioritario = false,
  showPriorizarButton = false,
  isPriorizando = false,
  isRemovendo = false,
  onPriorizar,
  onRemover,
  onVerPedidoOriginal,
  onRefetchPainel,
}) => {
  const [obsModalOpen, setObsModalOpen] = useState(false);
  const montagem = item.montagens?.[0];
  const fila = item as {
    startedAt?: string | null;
    finishedAt?: string | null;
    tempoTotal?: number | null;
  };
  const createdAt = item.createdAt;
  const inicioMontagem =
    item.status === "in_progress"
      ? montagem?.startedAt ?? fila.startedAt ?? null
      : null;
  const fimMontagem = montagem?.finishedAt ?? fila.finishedAt ?? null;
  const tempoTotal = montagem?.tempoTotal ?? fila.tempoTotal ?? null;

  let aguardandoMontagemSeg: number | null = null;
  if (inicioMontagem) {
    aguardandoMontagemSeg = segundosEntreInstantes(createdAt, inicioMontagem);
  } else if (item.status === "pending" && createdAt) {
    aguardandoMontagemSeg = segundosDesdeInicio(createdAt, nowMs);
  }

  let emMontagemSeg: number | null = null;
  if (fimMontagem && inicioMontagem) {
    emMontagemSeg =
      tempoTotal != null && tempoTotal >= 0
        ? tempoTotal
        : segundosEntreInstantes(inicioMontagem, fimMontagem);
  } else if (inicioMontagem) {
    emMontagemSeg = segundosDesdeInicio(inicioMontagem, nowMs);
  }

  const isSalao = isPedidoSalaoFoodfly({
    origem: item.origem,
    tipo: item.tipo,
  });
  const salaoLabels = isSalao
    ? resolveSalaoProducaoLabels({
        mesaId: item.pedidoFoodfly?.mesaId,
        comandaId: item.pedidoFoodfly?.comandaId,
        identificador: item.pedidoFoodfly?.identificador,
        displayId: item.displayId,
      })
    : null;
  /** Cabeçalho padronizado no shell do painel. */
  const headerBadges = isSalao ? (
    <SalaoProducaoOrderHeader
      labels={salaoLabels}
      showSalaoTag
      layout="stacked"
    />
  ) : undefined;
  const pedidoObservations = isSalao
    ? [item.pedidoFoodfly?.observacao]
    : [
        item.pedidoIntegracao?.observation,
        item.pedidoIntegracao?.internalObservation,
      ];
  const customerWeb = !isSalao
    ? item.pedidoIntegracao?.customerName ?? null
    : null;
  const isDoceFamilia = isFilaDoceFamilia(item.tipo);
  const tipoBadgeLabel = isSalao
    ? isDoceFamilia
      ? "Doce"
      : "Salgada"
    : filaTipoLabel(item.tipo);
  const isPending = item.status === "pending";
  const isEmMontagem = item.status === "in_progress";
  const isTipoComObsEditavel =
    item.tipo === "doce" ||
    item.tipo === "salgada" ||
    item.tipo === "salao_doce" ||
    item.tipo === "salao_salgada";
  const showObsButton =
    isTipoComObsEditavel && !!onRefetchPainel && (isPending || isEmMontagem);
  const showPriorizarControls =
    showPriorizarButton && isPending && !!onPriorizar;
  const showPedidoOriginal = !!onVerPedidoOriginal && !!item.orderId;
  const showActionRow =
    showPedidoOriginal || showObsButton || showPriorizarControls;

  const tempoColunaSeg = resolveTempoColunaFilaMontagem(item, nowMs);
  const urgencia = isPrioritario
    ? "normal"
    : resolvePainelCardUrgencia(tempoColunaSeg);

  const descItens = Array.isArray(item.descricao)
    ? item.descricao
    : item.descricao && typeof item.descricao === "object"
      ? [item.descricao]
      : [];
  const obsItensPizzaRaw = descItens
    .map((d: { observacao?: string | null }) => d?.observacao?.trim() || null)
    .filter(Boolean) as string[];
  const itemsRaw =
    (item as { itemsSalgados?: { observacao?: string | null } | null })
      .itemsSalgados ??
    (item as { itemsDoces?: { observacao?: string | null } | null }).itemsDoces;
  const obsFromProdutoCru =
    itemsRaw && typeof itemsRaw === "object" && !Array.isArray(itemsRaw)
      ? itemsRaw.observacao?.trim() || null
      : null;

  // Coluna EN da fila + pedido + descricao + produto cru FoodFly.
  const observacoesUnicas = dedupeObservacoes(
    item.observation,
    ...pedidoObservations,
    obsFromProdutoCru,
    ...obsItensPizzaRaw,
  );
  const observation = observacoesUnicas[0] ?? null;
  const obsItensPizza =
    observacoesUnicas.length > 1 ? observacoesUnicas.slice(1) : [];

  const tipoBadgeClass = isDoceFamilia
    ? "rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-amber-800"
    : "rounded-full bg-orange-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-orange-800";

  const headerEnd = (
    <>
      {!isSalao && (
        <OrderTypeBadge
          orderType={item.pedidoIntegracao?.orderType}
          iconSize={14}
          withTooltip={false}
        />
      )}
      <span className={`${tipoBadgeClass} whitespace-nowrap`}>
        {tipoBadgeLabel}
      </span>
    </>
  );

  return (
    <>
      <PainelCardShell
        variant={isPrioritario ? "prioritario" : "default"}
        urgencia={urgencia}
        isSalao={isSalao}
        displayId={item.displayId}
        headerBadges={headerBadges}
        badgeLabel={!isSalao ? `Pedido #${item.displayId}` : undefined}
        headerEnd={headerEnd}
      >
        <p className="mb-4 line-clamp-4 text-sm font-semibold leading-relaxed text-neutral-800 [[data-salao-card]_&]:text-white">
          {formatPainelDescricaoResumo(
            filaDescricaoJsonParaTexto(item.descricao)
          )}
        </p>

        <div>
          {!isSalao && customerWeb && (
            <PainelCardDetailRow icon={User}>{customerWeb}</PainelCardDetailRow>
          )}
          {item.assignedToUser?.nome && (
            <PainelCardDetailRow icon={ChefHat}>
              {item.assignedToUser.nome}
            </PainelCardDetailRow>
          )}
        </div>

        {observation && (
          <PainelCardAmberNote>{observation}</PainelCardAmberNote>
        )}

        {obsItensPizza.length > 0 && (
          <PainelCardAmberObsPizza>
            <span className="font-semibold">Obs. da pizza: </span>
            {obsItensPizza.join(" · ")}
          </PainelCardAmberObsPizza>
        )}

        <PainelCardTemposSection>
          <TemposEtapasBadges
            displayId={item.displayId}
            hidePedidoId={
              !!(salaoLabels?.mesa || salaoLabels?.comandaLabel)
            }
            aguardandoMontagemSeg={aguardandoMontagemSeg}
            emMontagemSeg={emMontagemSeg}
            emFornoSeg={null}
            aguardandoEntregaSeg={null}
            emEntregaSeg={null}
          />
        </PainelCardTemposSection>

        {showActionRow && (
          <PainelCardActionRow>
            {showObsButton && (
              <button
                type="button"
                onClick={() => setObsModalOpen(true)}
                className={`${PAINEL_ICON_BTN_CLASS} border border-amber-300 bg-amber-50 text-amber-900 transition-colors hover:bg-amber-100`}
                title="Observação da pizza"
                aria-label="Observação da pizza"
              >
                <MessageSquare size={PAINEL_ICON_SIZE} strokeWidth={2} />
              </button>
            )}
            {showPriorizarControls && (
              <button
                type="button"
                onClick={isPrioritario ? onRemover : onPriorizar}
                disabled={
                  isPrioritario
                    ? isRemovendo || isPriorizando
                    : isPriorizando || isPrioritario
                }
                title={
                  isPrioritario
                    ? isRemovendo
                      ? "Removendo prioridade…"
                      : "Remover prioridade"
                    : isPriorizando
                      ? "Priorizando…"
                      : "Priorizar na fila"
                }
                aria-label={
                  isPrioritario
                    ? isRemovendo
                      ? "Removendo prioridade"
                      : "Remover prioridade"
                    : isPriorizando
                      ? "Priorizando"
                      : "Priorizar na fila"
                }
                className={
                  isPrioritario
                    ? `${PAINEL_ICON_BTN_CLASS} border border-red-300 bg-white text-red-700 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70`
                    : `${PAINEL_ICON_BTN_CLASS} bg-red-600 text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-70`
                }
              >
                {isPrioritario ? (
                  isRemovendo ? (
                    <Loader2
                      size={PAINEL_ICON_SIZE}
                      strokeWidth={2}
                      className="animate-spin"
                    />
                  ) : (
                    <ArrowBigDown size={PAINEL_ICON_SIZE} strokeWidth={2} />
                  )
                ) : isPriorizando ? (
                  <Loader2
                    size={PAINEL_ICON_SIZE}
                    strokeWidth={2}
                    className="animate-spin"
                  />
                ) : (
                  <ArrowBigUp size={PAINEL_ICON_SIZE} strokeWidth={2} />
                )}
              </button>
            )}
            {showPedidoOriginal && onVerPedidoOriginal && (
              <PainelPedidoOriginalButton
                orderId={item.orderId}
                onOpen={onVerPedidoOriginal}
              />
            )}
          </PainelCardActionRow>
        )}
      </PainelCardShell>

      {onRefetchPainel && isTipoComObsEditavel && (
        <FilaItemObservacaoModal
          isOpen={obsModalOpen}
          onClose={() => setObsModalOpen(false)}
          tipo={item.tipo}
          filaId={item.id}
          descricao={item.descricao}
          onSaved={() => void onRefetchPainel()}
        />
      )}
    </>
  );
};
